import React from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import withStyles from 'react-jss'
import classnames from 'classnames'

import { buttonStyleBuilder } from '../../shared/button'
import IconTextTrackToggle from './icon-text-track-toggle'
import IconCheck from './icon-check'
import { selectTextTrack, toggleTextTrack } from '../../../utils/text-track'
import { segmentTrackSubtitleChanged } from '../../../../../segment/segment-track'

const getTrackLabel = (track, index) => track.label || track.language || `Captions ${index + 1}`
const CAPTION_PANEL_ID = 'player-subtitles-panel'
const SHOW_CAPTION_DIALOG_FOR_SINGLE_TRACK = true

const getCaptionPanelContainer = isFullscreen => {
  if (!isFullscreen) return document.body
  return document.fullscreenElement || document.webkitFullscreenElement || document.body
}

const trackCaptionChange = (contentItem, toggledOn, textTrackLabel) => {
  segmentTrackSubtitleChanged(
    contentItem,
    {
      toggledOn,
      ...(textTrackLabel ? { textTrackLabel } : {})
    }
  )
}

const styles = theme => {
  const hoverCheck = {
    backgroundColor: theme.color.primary,
    borderColor: theme.color.black,
    fill: theme.color.black
  }
  const buttonStyle = buttonStyleBuilder(theme)

  return {
    wrapper: {
      position: 'relative',
      animation: [['200ms', 1, 'ease-out', 'forwards']],
      animationName: '$enter',
      transform: 'scale(0)'
    },
    captionPanel: {
      position: 'fixed',
      right: 12,
      bottom: 60,
      width: 200,
      padding: [[8, 0]],
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      borderRadius: 10,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
      color: theme.color.white,
      opacity: 1,
      transform: 'scale(1) translate3d(0, 0, 0)',
      transition: [
        ['opacity', '300ms', 'ease'],
        ['transform', '180ms', 'ease']
      ]
    },
    hideCaptionPanel: {
      opacity: 0,
      transform: 'scale(0.5) translate3d(200px, 60px, 0)',
      pointerEvents: 'none'
    },
    panelTitle: {
      padding: [[10, 16, 12]],
      fontSize: 16,
      fontWeight: 600,
      fontFamily: 'GTWalsheim'
    },
    captionOption: {
      width: '100%',
      padding: [[10, 16]],
      border: 0,
      background: 'transparent',
      color: theme.color.white,
      cursor: 'pointer',
      fontSize: 18,
      fontFamily: 'GothamRounded',
      textAlign: 'left',
      '&:hover': {
        color: theme.color.primary
      }
    },
    selectedOption: {
      color: theme.color.primary
    },
    checkContainer: {
      position: 'absolute',
      left: 2,
      bottom: 10,
      width: 15,
      display: 'flex',
      opacity: 0,
      transform: 'scale(0)',
      transition: ['transform', '200ms', 'ease']
    },
    iconWrapper: buttonStyle.wrapper,
    iconButton: {
      ...buttonStyle.button,
      width: 48,
      '&:active': {
        transform: 'scale(0.8)'
      },
      '&:hover': {
        ...buttonStyle.button['&:hover'],
        '& $iconCheck': hoverCheck
      }
    },
    icon: {
      ...buttonStyle.icon
    },
    iconCheck: {
      fill: theme.color.white,
      backgroundColor: theme.color.black,
      borderRadius: 15,
      height: '100%',
      border: [[1, 'solid', theme.color.white]],
      transition: [
        ['border', 'ease-out', '200ms'],
        ['fill', 'ease-out', '200ms'],
        ['background', 'ease-out', '200ms']
      ]
    },
    activeCheck: {
      opacity: 1,
      transform: 'scale(1)'
    },
    '@keyframes enter': {
      0: {
        transform: 'scale(0)'
      },
      '100%': {
        transform: 'scale(1)'
      }
    }
  }
}

class TextTrackToggleUi extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      selectedTrack: null
    }
  }

  componentDidMount() {
    const { isTextTrackShown, textTracks } = this.props
    const selectedTrack = textTracks.find(track => track.mode === 'showing') || textTracks[0]
    this.setState({ selectedTrack })
    selectTextTrack(textTracks, isTextTrackShown ? selectedTrack : null)
  }

  componentDidUpdate(prevProps) {
    const { isTextTrackShown } = this.props
    if (isTextTrackShown !== prevProps.isTextTrackShown) {
      this.showOrHideTextTrack(isTextTrackShown)
    }
  }

  // eslint-disable-next-line no-undef
  showOrHideTextTrack = shouldShowTextTrack => {
    const { textTracks } = this.props
    const { selectedTrack } = this.state
    if (textTracks.length > 1) {
      selectTextTrack(textTracks, shouldShowTextTrack ? selectedTrack || textTracks[0] : null)
    } else {
      toggleTextTrack(shouldShowTextTrack, textTracks[0])
    }
  }

  selectTrack = track => {
    const {
      textTracks, saveCloseCaption, toggleCaptionPanelOpen, contentItem
    } = this.props
    selectTextTrack(textTracks, track)
    saveCloseCaption(Boolean(track))
    this.setState({ selectedTrack: track })
    toggleCaptionPanelOpen()
    trackCaptionChange(
      contentItem,
      Boolean(track),
      track ? getTrackLabel(track, textTracks.indexOf(track)) : undefined
    )
  }

  render() {
    const {
      saveCloseCaption,
      isTextTrackShown,
      classes,
      textTracks,
      contentItem,
      isCaptionPanelOpen,
      isFullscreen,
      toggleCaptionPanelOpen
    } = this.props
    const { selectedTrack } = this.state
    const shouldShowCaptionPanel = SHOW_CAPTION_DIALOG_FOR_SINGLE_TRACK || textTracks.length > 1

    return (
      <div
        className={classes.wrapper}
        onClick={evt => {
          evt.stopPropagation()
          if (shouldShowCaptionPanel) {
            toggleCaptionPanelOpen()
            return
          }
          const toggled = !isTextTrackShown
          saveCloseCaption(toggled)
          this.showOrHideTextTrack(toggled)
          trackCaptionChange(
            contentItem,
            toggled,
            toggled ? getTrackLabel(textTracks[0], 0) : undefined
          )
        }}
      >
        {/* Captions selector */}
        {shouldShowCaptionPanel && ReactDOM.createPortal(
          <div
            id={CAPTION_PANEL_ID}
            className={classnames(classes.captionPanel, {
              [classes.hideCaptionPanel]: !isCaptionPanelOpen
            })}
            aria-hidden={!isCaptionPanelOpen}
            onClick={evt => evt.stopPropagation()}
            onDoubleClick={evt => evt.stopPropagation()}
          >
            <div className={classes.panelTitle}>Subtitles</div>
            <button
              type="button"
              tabIndex={isCaptionPanelOpen ? 0 : -1}
              className={classnames(classes.captionOption, {
                [classes.selectedOption]: !isTextTrackShown
              })}
              onClick={evt => {
                evt.stopPropagation()
                this.selectTrack(null)
              }}
            >
              Off
            </button>
            {textTracks.map((track, index) => (
              <button
                type="button"
                tabIndex={isCaptionPanelOpen ? 0 : -1}
                className={classnames(classes.captionOption, {
                  [classes.selectedOption]: isTextTrackShown && selectedTrack === track
                })}
                key={track.id || `${track.language}-${index}`}
                onClick={evt => {
                  evt.stopPropagation()
                  this.selectTrack(track)
                }}
              >
                {getTrackLabel(track, index)}
              </button>
            ))}
          </div>,
          getCaptionPanelContainer(isFullscreen)
        )}
        {/* Caption toggle */}
        <div className={classes.iconWrapper}>
          <button
            type="button"
            className={classes.iconButton}
            aria-label="Subtitles"
            aria-expanded={shouldShowCaptionPanel ? isCaptionPanelOpen : undefined}
            aria-controls={shouldShowCaptionPanel ? CAPTION_PANEL_ID : undefined}
          >
            <IconTextTrackToggle className={classes.icon} />
            <div
              className={classnames(classes.checkContainer, {
                [classes.activeCheck]: isTextTrackShown
              })}
            >
              <IconCheck className={classes.iconCheck} />
            </div>
          </button>
        </div>
      </div>
    )
  }
}

TextTrackToggleUi.propTypes = {
  isTextTrackShown: PropTypes.bool.isRequired,
  saveCloseCaption: PropTypes.func.isRequired,
  isCaptionPanelOpen: PropTypes.bool.isRequired,
  isFullscreen: PropTypes.bool.isRequired,
  toggleCaptionPanelOpen: PropTypes.func.isRequired,
  contentItem: PropTypes.shape({
    title: PropTypes.string.isRequired,
    ldId: PropTypes.string.isRequired,
    __typename: PropTypes.string.isRequired
  }).isRequired,
  textTracks: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    kind: PropTypes.string.isRequired,
    label: PropTypes.string,
    language: PropTypes.string,
    mode: PropTypes.string
  })).isRequired
}

export default withStyles(styles)(TextTrackToggleUi)
